import React, { createContext, useContext, useState, useCallback } from 'react';

const I18nContext = createContext(null);

const translations = {
  de: {
    nav: {
      discover: 'Entdecken', login: 'Anmelden', register: 'Registrieren',
      logout: 'Abmelden', dashboard: 'Dashboard', newListing: 'Neues Inserat',
      rent: 'Vermieten', messages: 'Nachrichten', profile: 'Profil', search: 'Suche nach Gegenständen...',
    },
    landing: {
      subtitle: 'Die Sharing-Plattform', title1: 'Miete alles.', title2: 'Vermiete alles.',
      desc: 'Von Werkzeugen bis Elektronik, von Fahrzeugen bis Sportgeräte. Teile was du hast, leihe was du brauchst.',
      searchPlaceholder: 'Was suchst du?', searchBtn: 'Suchen',
      categoriesLabel: 'Kategorien', categoriesTitle: 'Was brauchst du?', showAll: 'Alle anzeigen',
      featuredLabel: 'Neu eingestellt', featuredTitle: 'Aktuelle Angebote', allOffers: 'Alle Angebote',
      howLabel: "So funktioniert's", howTitle: 'In 3 Schritten loslegen',
      step1: 'Finden', step1desc: 'Durchsuche tausende Gegenstände in deiner Nähe.',
      step2: 'Buchen', step2desc: 'Wähle deinen Zeitraum und sende eine Anfrage.',
      step3: 'Nutzen', step3desc: 'Bezahle sicher online und hole deinen Gegenstand ab.',
      ctaTitle: 'Bereit zum Vermieten?', ctaDesc: 'Verdiene Geld mit Dingen, die du nicht täglich brauchst.',
      ctaBtn: 'Jetzt starten',
    },
    auth: {
      welcomeBack: 'Willkommen zurück', loginDesc: 'Melde dich an, um fortzufahren.',
      email: 'E-Mail', password: 'Passwort', loginBtn: 'Anmelden', loggingIn: 'Anmelden...',
      orSeparator: 'oder', googleLogin: 'Mit Google anmelden', googleRegister: 'Mit Google registrieren',
      noAccount: 'Noch kein Konto?', toRegister: 'Registrieren',
      createAccount: 'Konto erstellen', registerDesc: 'Werde Teil der Sharing-Community.',
      name: 'Name', namePlaceholder: 'Dein Name', registerBtn: 'Konto erstellen',
      registering: 'Registrieren...', hasAccount: 'Bereits registriert?', toLogin: 'Anmelden',
      passwordMin: 'Min. 6 Zeichen', passwordError: 'Passwort muss mindestens 6 Zeichen lang sein.',
      referralCode: 'Empfehlungscode', referralPlaceholder: 'Optional: Code eingeben',
    },
    listings: {
      results: 'Ergebnisse', allCategories: 'Alle Kategorien', all: 'Alle',
      minPrice: 'Min. Preis', maxPrice: 'Max. Preis', apply: 'Anwenden',
      reset: 'Zurücksetzen', filter: 'Filter', noResults: 'Keine Ergebnisse gefunden.',
      perDay: '/ Tag', noImage: 'Kein Bild', search: 'Suche:', back: 'Zurück',
      provider: 'Anbieter', selectPeriod: 'Zeitraum wählen', from: 'Von', to: 'Bis',
      selectDate: 'Datum wählen', days: 'Tage', day: 'Tag', bookMessage: 'Nachricht an den Vermieter (optional)',
      sendRequest: 'Anfrage senden', sending: 'Wird gesendet...', editListing: 'Inserat bearbeiten',
      requestSent: 'Buchungsanfrage gesendet!', messageOwner: 'Nachricht senden',
    },
    create: {
      newListing: 'Neues Inserat', editListing: 'Inserat bearbeiten',
      newDesc: 'Stelle einen Gegenstand zur Vermietung ein.',
      editDesc: 'Aktualisiere die Details deines Inserats.',
      images: 'Bilder', add: 'Hinzufügen', title: 'Titel', titlePlaceholder: 'z.B. Bosch Akku-Bohrschrauber',
      description: 'Beschreibung', descPlaceholder: 'Beschreibe den Gegenstand, Zustand, Zubehör...',
      category: 'Kategorie', selectCategory: 'Kategorie wählen',
      pricePerDay: 'Preis / Tag (EUR)', location: 'Standort', locationPlaceholder: 'z.B. Berlin',
      save: 'Inserat erstellen', update: 'Aktualisieren', saving: 'Speichern...', cancel: 'Abbrechen',
      updated: 'Inserat aktualisiert!', created: 'Inserat erstellt!', fillAll: 'Bitte fülle alle Pflichtfelder aus.',
    },
    dashboard: {
      title: 'Dashboard', welcome: 'Willkommen',
      myListings: 'Meine Inserate', myRentals: 'Meine Buchungen', requests: 'Anfragen',
      noListings: 'Du hast noch keine Inserate.', createFirst: 'Erstes Inserat erstellen',
      noRentals: 'Du hast noch nichts gemietet.', discoverOffers: 'Angebote entdecken',
      noRequests: 'Keine Anfragen vorhanden.',
      accept: 'Akzeptieren', reject: 'Ablehnen', payNow: 'Jetzt bezahlen',
      accepted: 'Anfrage akzeptiert', rejected: 'Anfrage abgelehnt',
      deleteConfirm: 'Inserat wirklich löschen?', deleted: 'Inserat gelöscht',
      stats: { listings: 'Meine Inserate', rentals: 'Meine Buchungen', requestsReceived: 'Anfragen erhalten' },
      review: 'Bewertung abgeben', payWithPaypal: 'PayPal', payWithStripe: 'Kreditkarte',
    },
    status: {
      pending: 'Ausstehend', accepted: 'Akzeptiert', rejected: 'Abgelehnt',
      paid: 'Bezahlt', completed: 'Abgeschlossen', cancelled: 'Storniert',
      active: 'Aktiv', inactive: 'Inaktiv',
    },
    reviews: {
      title: 'Bewertungen', writeReview: 'Bewertung schreiben', noReviews: 'Noch keine Bewertungen.',
      rating: 'Bewertung', reviewText: 'Deine Bewertung', submit: 'Absenden', submitted: 'Bewertung abgegeben!',
      stars: 'Sterne',
    },
    referral: {
      title: 'Empfehlungsprogramm', subtitle: 'Lade Freunde ein und verdiene Guthaben',
      yourCode: 'Dein Empfehlungscode', copy: 'Kopieren', copied: 'Kopiert!',
      desc: 'Teile deinen Code. Dein Freund bekommt 10% Rabatt auf die erste Buchung und du erhältst 5 EUR Guthaben.',
      stats: 'Deine Statistiken', totalReferrals: 'Empfehlungen', totalEarned: 'Verdient',
      currentCredits: 'Aktuelles Guthaben', history: 'Empfehlungshistorie',
    },
    messages: {
      title: 'Nachrichten', noConversations: 'Keine Nachrichten vorhanden.',
      typePlaceholder: 'Nachricht schreiben...', send: 'Senden',
      startConversation: 'Konversation starten',
    },
    profile: {
      title: 'Profil bearbeiten', nameLabel: 'Name', saveBtn: 'Speichern',
      saved: 'Profil gespeichert!', uploadPicture: 'Profilbild hochladen',
      memberSince: 'Mitglied seit', activeListings: 'Aktive Inserate',
      avgRating: 'Durchschnitt', reviewCount: 'Bewertungen',
    },
    payment: {
      checking: 'Zahlung wird überprüft...', wait: 'Bitte warte einen Moment.',
      success: 'Zahlung erfolgreich!', successDesc: 'Deine Buchung wurde bestätigt und die Zahlung verarbeitet.',
      amount: 'Betrag', status: 'Status', paidStatus: 'Bezahlt',
      backToDashboard: 'Zum Dashboard', failed: 'Zahlung fehlgeschlagen',
      timeout: 'Status unbekannt', timeoutDesc: 'Die Überprüfung hat zu lange gedauert.',
      failedDesc: 'Die Zahlung konnte nicht verarbeitet werden.',
      chooseMethod: 'Zahlungsmethode wählen',
    },
    common: { eur: 'EUR', perDay: '/ Tag', loading: 'Laden...', error: 'Fehler' },
  },
  en: {
    nav: {
      discover: 'Discover', login: 'Sign In', register: 'Sign Up',
      logout: 'Sign Out', dashboard: 'Dashboard', newListing: 'New Listing',
      rent: 'Rent Out', messages: 'Messages', profile: 'Profile', search: 'Search for items...',
    },
    landing: {
      subtitle: 'The Sharing Platform', title1: 'Rent anything.', title2: 'Share everything.',
      desc: 'From tools to electronics, vehicles to sports gear. Share what you have, borrow what you need.',
      searchPlaceholder: 'What are you looking for?', searchBtn: 'Search',
      categoriesLabel: 'Categories', categoriesTitle: 'What do you need?', showAll: 'View all',
      featuredLabel: 'Recently added', featuredTitle: 'Latest Listings', allOffers: 'All Listings',
      howLabel: 'How it works', howTitle: 'Get started in 3 steps',
      step1: 'Find', step1desc: 'Browse thousands of items near you.',
      step2: 'Book', step2desc: 'Choose your dates and send a request.',
      step3: 'Use', step3desc: 'Pay securely online and pick up your item.',
      ctaTitle: 'Ready to rent out?', ctaDesc: 'Earn money from things you don\'t use every day.',
      ctaBtn: 'Get started',
    },
    auth: {
      welcomeBack: 'Welcome back', loginDesc: 'Sign in to continue.',
      email: 'Email', password: 'Password', loginBtn: 'Sign In', loggingIn: 'Signing in...',
      orSeparator: 'or', googleLogin: 'Sign in with Google', googleRegister: 'Sign up with Google',
      noAccount: 'Don\'t have an account?', toRegister: 'Sign Up',
      createAccount: 'Create Account', registerDesc: 'Join the sharing community.',
      name: 'Name', namePlaceholder: 'Your name', registerBtn: 'Create Account',
      registering: 'Creating...', hasAccount: 'Already have an account?', toLogin: 'Sign In',
      passwordMin: 'Min. 6 characters', passwordError: 'Password must be at least 6 characters.',
      referralCode: 'Referral Code', referralPlaceholder: 'Optional: enter code',
    },
    listings: {
      results: 'Results', allCategories: 'All Categories', all: 'All',
      minPrice: 'Min Price', maxPrice: 'Max Price', apply: 'Apply',
      reset: 'Reset', filter: 'Filter', noResults: 'No results found.',
      perDay: '/ day', noImage: 'No image', search: 'Search:', back: 'Back',
      provider: 'Provider', selectPeriod: 'Select period', from: 'From', to: 'To',
      selectDate: 'Select date', days: 'days', day: 'day', bookMessage: 'Message to owner (optional)',
      sendRequest: 'Send request', sending: 'Sending...', editListing: 'Edit listing',
      requestSent: 'Booking request sent!', messageOwner: 'Send message',
    },
    create: {
      newListing: 'New Listing', editListing: 'Edit Listing',
      newDesc: 'List an item for rent.',
      editDesc: 'Update your listing details.',
      images: 'Images', add: 'Add', title: 'Title', titlePlaceholder: 'e.g. Bosch Cordless Drill',
      description: 'Description', descPlaceholder: 'Describe the item, condition, accessories...',
      category: 'Category', selectCategory: 'Select category',
      pricePerDay: 'Price / Day (EUR)', location: 'Location', locationPlaceholder: 'e.g. Berlin',
      save: 'Create Listing', update: 'Update', saving: 'Saving...', cancel: 'Cancel',
      updated: 'Listing updated!', created: 'Listing created!', fillAll: 'Please fill in all required fields.',
    },
    dashboard: {
      title: 'Dashboard', welcome: 'Welcome',
      myListings: 'My Listings', myRentals: 'My Rentals', requests: 'Requests',
      noListings: 'You have no listings yet.', createFirst: 'Create first listing',
      noRentals: 'You haven\'t rented anything yet.', discoverOffers: 'Discover offers',
      noRequests: 'No requests yet.',
      accept: 'Accept', reject: 'Decline', payNow: 'Pay now',
      accepted: 'Request accepted', rejected: 'Request declined',
      deleteConfirm: 'Really delete this listing?', deleted: 'Listing deleted',
      stats: { listings: 'My Listings', rentals: 'My Rentals', requestsReceived: 'Requests Received' },
      review: 'Write Review', payWithPaypal: 'PayPal', payWithStripe: 'Credit Card',
    },
    status: {
      pending: 'Pending', accepted: 'Accepted', rejected: 'Declined',
      paid: 'Paid', completed: 'Completed', cancelled: 'Cancelled',
      active: 'Active', inactive: 'Inactive',
    },
    reviews: {
      title: 'Reviews', writeReview: 'Write Review', noReviews: 'No reviews yet.',
      rating: 'Rating', reviewText: 'Your review', submit: 'Submit', submitted: 'Review submitted!',
      stars: 'Stars',
    },
    referral: {
      title: 'Referral Program', subtitle: 'Invite friends and earn credits',
      yourCode: 'Your Referral Code', copy: 'Copy', copied: 'Copied!',
      desc: 'Share your code. Your friend gets 10% off their first booking and you earn 5 EUR credit.',
      stats: 'Your Statistics', totalReferrals: 'Referrals', totalEarned: 'Earned',
      currentCredits: 'Current Credits', history: 'Referral History',
    },
    messages: {
      title: 'Messages', noConversations: 'No messages yet.',
      typePlaceholder: 'Type a message...', send: 'Send',
      startConversation: 'Start conversation',
    },
    profile: {
      title: 'Edit Profile', nameLabel: 'Name', saveBtn: 'Save',
      saved: 'Profile saved!', uploadPicture: 'Upload profile picture',
      memberSince: 'Member since', activeListings: 'Active Listings',
      avgRating: 'Average', reviewCount: 'Reviews',
    },
    payment: {
      checking: 'Checking payment...', wait: 'Please wait a moment.',
      success: 'Payment successful!', successDesc: 'Your booking has been confirmed and payment processed.',
      amount: 'Amount', status: 'Status', paidStatus: 'Paid',
      backToDashboard: 'Back to Dashboard', failed: 'Payment failed',
      timeout: 'Status unknown', timeoutDesc: 'The verification took too long.',
      failedDesc: 'The payment could not be processed.',
      chooseMethod: 'Choose payment method',
    },
    common: { eur: 'EUR', perDay: '/ day', loading: 'Loading...', error: 'Error' },
  },
};

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem('lang');
    return saved || 'de';
  });

  const switchLang = useCallback((newLang) => {
    setLang(newLang);
    localStorage.setItem('lang', newLang);
  }, []);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let val = translations[lang];
    for (const k of keys) {
      val = val?.[k];
    }
    return val || key;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, switchLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
